import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import * as Blockly from 'blockly/core';

import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditIcon from "@mui/icons-material/Edit";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import { Box, Button, Container, Divider, IconButton, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { UseTreeViewItemsPublicAPI } from '@mui/x-tree-view/internals/plugins/useTreeViewItems/useTreeViewItems.types';

import '@uiw/react-markdown-preview/markdown.css';
import '@uiw/react-md-editor/markdown-editor.css';

import CustomTooltip from '../../components/common/CustomTooltip';
import { AddDialog, CustomTreeItem, FileType } from '../../components/file-explorer';
import { deleteFileStatesKeys, handleAddNewFile, handleCreateNewFolder, handleDeleteTreeItem, handleEditTreeItemCallBack } from '../../components/file-explorer/utils';
import PrimaryLayout from '../../components/layout/PrimaryLayout';
import BasicInfoModal from '../../components/modals/BasicInfoModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import VariableInputParams from '../../components/modals/VariableInputModal';
import { PythonRunner } from '../../pyodide';
import { axiosRepoIntance } from '../../services/api-client';
import { ChangeFileOperation, ChangeFilesOptions, GitEntry, GitTreeResponse, RepoCommit } from '../../services/repo-service';
import { Block_Default_Files, BLOCK_FILE_PATH, BLOCK_TYPE, getSecondaryBlocklycode } from '../../utils/constants/block';
import { base64ToUtf8, convertGitTreeToTreeView, getFilePathsAndContents, OWNER, ReposByBlockType, utf8ToBase64 } from '../../utils/constants/gitea';
import { getFormattedDate } from '../../utils/constants/text-field';
import { BasicInfoForm } from '../../utils/models/basic-info';
import { BlockVariables } from '../../utils/models/blocks';
import { CustomTreeViewBaseItem, FilePathContent, FileState } from '../../utils/models/common-models';
import BlockTypeEditors from './block-types';
import { getInputVariables } from './get-variables';


declare let BlockMirror: any;

const supportedFileType: { [key: string]: FileType } = {
    "py": "py",
    "json": "json",
    "md": "md",
    "txt": "txt",
    "folder": "folder",
    "file": "file"
}

const isBinaryFile = (file_name: string): boolean => {
    return file_name !== BLOCK_FILE_PATH.dockerfile ? !supportedFileType[file_name.split('.').splice(1)[0]] : false
}


const AddBlock: React.FC = () => {
    const pythonFileInputRef = useRef<HTMLInputElement | null>(null);
    const apiTreeRef = useTreeViewApiRef();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: updateBlockId, name: updateBlockName, type: blockType } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [basicInfo, setBasicInfo] = useState<BasicInfoForm>({ name: "", description: "", tags: [] });
    const [openBasicInfoModal, setOpenBasicInfoModal] = useState(false);
    // const [blocksType, setBlocksType] = useState<BLOCKS_TYPE.TRANSFORMER | BLOCKS_TYPE.EVALUATOR>(BLOCKS_TYPE.TRANSFORMER);
    const [blockVersion, setBlockVersion] = useState<string>("");
    const [openInputParamsModal, setOpenInputParamsModal] = useState<boolean>(false);
    // const [outputDrawer, setOutputDrawer] = useState<boolean>(false);
    // const [pythonFunctionCall, setPythonFunctionCall] = useState<string>("")
    // const [outputDrawerResult, setOutputDrawerResult] = useState<string>("")
    const [inputVariables, setInputVariables] = useState<BlockVariables[]>([])
    // const [outputVariables, setOutputVariables] = useState<BlockVariables[]>([]);
    const [pyodideInstance, setPyodideInstance] = useState<PythonRunner>();
    const [commitsList, setCommitsList] = useState<{ commitId: string, created: string }[]>([]);

    const [blocklyEditor, setBlocklyEditor] = useState<any>();
    const blocklyEditorRef = useRef<any>(blocklyEditor);

    const [fileStructure, setFileStructure] = useState<CustomTreeViewBaseItem[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [newFileName, setNewFileName] = useState<string>('');
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [isAddingFolder, setIsAddingFolder] = useState<boolean>(false);
    const [isEditModeTreeItem, setIsEditModeTreeItem] = useState<boolean>(false)    // Edit mode for file-explorer
    const [openDeleteModalFileStucture, setOpenDeleteModalFileStucture] = useState<boolean>(false);
    const [itemsToDeletePath, setItemsToDeletePath] = useState<string[]>([]);       // paths of items to delete file-stucture

    const [selectedFile, setSelectedFile] = useState<string>('');
    const [fileStates, setFileStates] = useState<FileState>({}); // Store states for all files
    const [gitTreeContent, setGitTreeContent] = useState<GitEntry[]>([]);


    const updateBasicInfo = (data: BasicInfoForm) => {
        console.log("basic info: ", data);
        setBasicInfo(data);
    };

    const getInputVariablesList = (): BlockVariables[] => {

        const workspace = blocklyEditor.blockEditor.workspace as Blockly.Workspace
        let paramVariables: BlockVariables[] = [];

        workspace.getBlocksByType("ast_FunctionParameter", true).some(block => {
            console.log("input block: ", block.getVarModels()[0])
            const variable = block.getVarModels()[0];

            if (variable.name === "param") {
                paramVariables = [];
                console.log("--------------please select all variables--------------")
                return true;
            }

            paramVariables.push({ name: variable.name, type: variable.type === "Number" ? "number" : "string" })
            return false;
        })

        console.log("inputVariables list: ", paramVariables)
        setInputVariables(paramVariables);

        return paramVariables;
    }

    const getOutputVariablesList = () => {
        const workspace = blocklyEditor.blockEditor.workspace as Blockly.Workspace;
        let outputVars: BlockVariables[] = [];

        const tupleArray = workspace.getBlocksByType("ast_Tuple", true);
        const returnTuple = tupleArray[tupleArray.length - 1]

        returnTuple.childBlocks_.some(child => {
            console.log("output block: ", child.getVarModels()[0])
            const variable = child.getVarModels()[0];

            if (variable.name === 'param') {
                outputVars = [];
                console.log("--------------please select all variables--------------")
                return true;
            }

            outputVars.push({ name: variable.name, type: variable.type === "Number" ? "number" : "string" });
            return false;
        })

        console.log("output variables list: ", outputVars)
        // setOutputVariables(outputVars);

        return outputVars;
    }

    const handleTestClick = () => {
        if (!pyodideInstance) return;

        getInputVariables(blocklyEditor.getCode(), pyodideInstance)

        // const inputVarList = getInputVariablesList();
        // if (inputVarList.length === 0) {
        //     console.log("please select all variable first.")
        //     return;
        // }
        // setOpenInputParamsModal(true)
    }

    const handleRunPython = (inputParams: { [key: string]: string | number }) => {
        console.log("input params with values: ", inputParams);
        const workspace = blocklyEditor.blockEditor.workspace as Blockly.Workspace;

        const functionName = workspace.getBlocksByType('ast_FunctionDef', true)[0].getFieldValue('NAME')
        console.log('function name: ', functionName);

        const inputParamKeys = Object.keys(inputParams);
        let functionCallStatement = `print(${functionName}(`

        for (let i = 0; i < inputParamKeys.length; i++) {
            console.log('key: ', inputParamKeys[i])
            functionCallStatement += typeof inputParams[inputParamKeys[i]] === 'string' ?
                `'${inputParams[inputParamKeys[i]]}'` : `${inputParams[inputParamKeys[i]]}`;

            if (i !== inputParamKeys.length - 1) {
                functionCallStatement = functionCallStatement + `, `
            }
        }
        functionCallStatement = functionCallStatement + `))`;
        // setPythonFunctionCall(functionCallStatement);

        let pythonCode = `${blocklyEditor.getCode()}\n${functionCallStatement}`
        console.log("python code:\n", pythonCode)

        if (pyodideInstance) {
            pyodideInstance.run(pythonCode)
            // setOutputDrawerResult(pyodideInstance?.getResult());
            // setOutputDrawer(true);
        }
    }

    // in edit mode fetch selected files
    const handleFileSelect = useCallback(async (fileId: string, filepath: string) => {
        if (!blockType) return;

        try {
            const response = await getBlockFileFromCommit(filepath, updateBlockId || "", ReposByBlockType[blockType]);
            if (response === 'undefined') return;

            console.log("block file data---> ", filepath,);
            setFileStates(prevData => ({ ...prevData, [fileId]: response }))

            if (fileId.endsWith('.py')) {
                blocklyEditorRef.current?.setCode(response, undefined, false)
            }
        } catch (error) {
            console.error(`Error fetching content for ${fileId}: `, error);
            alert('Failed to fetch file content.');
        }
    }, [blockType, updateBlockId])

    const handleSaveBlock = () => {
        if (!basicInfo.name) {
            alert("Please provide the name.")
            setOpenBasicInfoModal(true)
            return;
        }

        const filesData: FilePathContent[] = getFilePathsAndContents(fileStructure, fileStates);
        console.log("paths------", filesData)

        const gitTreePaths = new Set(gitTreeContent.map(item => item.path));
        console.log('git tree paths: ', gitTreePaths)

        const files: ChangeFileOperation[] = filesData.map(data => {
            const isBinary = isBinaryFile(data.filename);

            return {
                operation: gitTreePaths.has(data.path) ? 'update' : 'create',
                path: data.path,
                content: utf8ToBase64(data.content, isBinary)
            }
        });

        itemsToDeletePath.forEach(path => {
            if (gitTreePaths.has(path)) {
                files.push({ operation: 'delete', path })
            }
        });

        console.log("creating/updating files: ", files)

        creatBlockWithFiles(basicInfo.name, basicInfo.description, files);
    }

    const handleImportBlockCode = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB limit

        if (files.length === 0) {
            return alert("No files selected.");
        }

        files.forEach(file => {
            if (file.size > MAX_FILE_SIZE) {
                alert(`The file "${file.name}" exceeds the size limit of 10MB.`);
                return;
            }

            const reader = new FileReader();
            isBinaryFile(file.name) ? reader.readAsArrayBuffer(file) : reader.readAsText(file);

            reader.onload = (e: ProgressEvent<FileReader>) => {
                const fileContent = reader.result;
                setFileStates(prev => ({ ...prev, [file.name]: fileContent }))

                if (!Block_Default_Files[blockType || ''].includes(file.name) && !fileStates[file.name]) {
                    handleCreateNewFile(file.name);
                }
                setSelectedFile(BLOCK_FILE_PATH.readme)
            };

            reader.onerror = (error) => {
                console.error('Error reading file:', error);
                alert(`Error reading file: ${file.name}`);
            };
        });

        event.target.value = "";
    }

    const handleExportBlockCode = () => {
        const pythonCode = blocklyEditor.getCode();
        const blockName = basicInfo.name;
        if (!blockName) {
            alert("Please provide block name.")
            return;
        }

        const blob = new Blob([pythonCode], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${blockName}.py`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const handleAddFileClick = () => {
        setIsEditModeTreeItem(false);
        setIsAddingFolder(false);
        setIsDialogOpen(true);
    };

    const handleAddFolderClick = () => {
        setIsEditModeTreeItem(false);
        setIsAddingFolder(true);
        setIsDialogOpen(true);
    };

    const handleCancel = () => {
        setIsDialogOpen(false);
        setNewFileName('');
        setNewFolderName("");
    };

    const handleEditTreeItem = () => {
        if (!selectedFile) return alert("Please select an item first.");

        if (Block_Default_Files[blockType || ''].includes(selectedFile)) {
            return alert("Default file names cannot be edited.");
        }

        setIsEditModeTreeItem(true);
        const selectedTreeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(selectedFile);

        if (selectedTreeItem.type === 'folder') {
            setIsAddingFolder(true);
            setIsDialogOpen(true);
            setNewFolderName(selectedTreeItem.label);
        }
        else {
            setIsAddingFolder(false);
            setIsDialogOpen(true);
            setNewFileName(selectedTreeItem.label);
        }
    };

    const handleCreateNewFile = (file_name?: string) => {
        // handleAddNewFile(selectedFile, newFileName, apiTreeRef, supportedFileType, setFileStructure, setIsDialogOpen, setNewFileName, file_name);
        handleAddNewFile(
            selectedFile,
            newFileName,
            apiTreeRef as React.MutableRefObject<any>,
            supportedFileType,
            setFileStructure,
            setIsDialogOpen,
            setNewFileName,
            file_name
          );
          
    };

    const creatBlockWithFiles = async (block_name: string, message: string, files: ChangeFileOperation[]) => {
        if (!blockType) return;

        setIsLoading(true);
        let payload: ChangeFilesOptions = {
            message,
            files,
        }

        if (updateBlockId) {
            payload = { ...payload, branch: block_name }
        }
        else {
            payload = { ...payload, branch: "main", new_branch: block_name, }
        }

        try {
            const response = await axiosRepoIntance.repos.repoChangeFiles(OWNER, ReposByBlockType[blockType], payload);
            console.log("create block response: ", response)
            if (response.status === 201) {
                navigate(`/blocks/${blockType}`);
            }
        } catch (error: any) {
            if (error?.response?.status === 422) {
                return alert("Block name with " + block_name + " already exists.");
            }

            alert("some error occurred while creating block.")
            console.log("erron in create block: ", error)
        } finally {
            setIsLoading(false);
        }
    }

    const getContentsListForCommit = async (commitId: string, repoName: string) => {
        setIsLoading(true);
        console.log('fetching data from------> ', commitId)

        try {
            const response = await axiosRepoIntance.repos.getTree(OWNER, repoName, commitId, { recursive: true })
            // const response = await axiosRepoIntance.repos.repoGetContentsList(OWNER, Repo_Type.blocks,  { ref: commitId })
            return response.data;
        } catch (error: any) {
            alert("some error occured while getting block.py file")
            console.log("some error occured while getting block.py file: ", error)
            return [] as GitTreeResponse;
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    }

    const getBlockFileFromCommit = async (filepath: string, commitId: string, repoName: string) => {
        setIsLoading(true);

        try {
            const response = await axiosRepoIntance.repos.repoGetContents(OWNER, repoName, filepath, { ref: commitId });
            const content = base64ToUtf8(response.data.content || "");

            // Log the content for debugging purposes
            // console.log(`Fetched content for ${filepath}: `, content);

            return content; // Return the decoded content
        } catch (error: any) {
            if (error?.response?.status === 404) {
                const file_name = filepath.split('/').pop();
                return alert(file_name + " file not found!");
            }

            console.error("Error fetching file content: ", error);
            alert("An error occurred while fetching the file content");
            // throw error; // Rethrow the error to handle it in the calling function
            return "";
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    };

    const getAllCommitsList = async (blockName: string, repoName: string) => {
        try {
            const response = await axiosRepoIntance.repos.repoGetAllCommits(OWNER, repoName, { sha: blockName })
            console.log("list of all commits: ", response.data);
            const list: { commitId: string, created: string, commit: RepoCommit }[] = [];

            response.data.forEach((data: any) => {
                if (data.commit.message === 'Initial commit\n') return;

                list.push({ commitId: data.sha, created: data.created, commit: data.commit });
            })

            setCommitsList(list);
            return list;
        } catch (error: any) {
            if (error?.response?.status === 404) {
                return alert("Some error occurred, Block versions not found!");
            }

            alert("some error occured while getting commits list.")
            console.log("error while getting the commits list: ", error)
        }
    }

    useEffect(() => {
        const container = document.getElementById('blockmirror-editor');
        const publicUrl = process.env.PUBLIC_URL;
        const isPythonFile = selectedFile?.endsWith(".py");

        if (container) {
            if (isPythonFile) {
                if (!blocklyEditor) {
                    const configuration = {
                        container,
                        // height: window.innerHeight - 150,
                        height: window.innerHeight - 100,
                        // toolbox: BLOCKS_TYPE.TRANSFORMER,
                        blocklyMediaPath: `${publicUrl}/assets/js/blockly/media/`,
                    };

                    const editor = new BlockMirror(configuration);
                    const initialCode = fileStates[selectedFile] || "";

                    // console.log('Setting Python code in use-effect:', selectedFile);
                    editor.setCode(initialCode, undefined, false);

                    setBlocklyEditor(editor);
                    blocklyEditorRef.current = editor;
                    console.log("BlockMirror editor initialized:", editor);
                }

            }
        }
        else if (!container) {
            console.log("Container for BlockMirror not found.");
        }

        return () => {
            if (blocklyEditor && !selectedFile?.endsWith(".py")) {
                (blocklyEditor.blockEditor.workspace as Blockly.Workspace).dispose();
                setBlocklyEditor(null);
                blocklyEditorRef.current = null;
                console.log("BlockMirror editor disposed.");
            }
        };
    }, [blocklyEditor, fileStates, selectedFile]);

    // added change listner for Blockly
    useEffect(() => {
        if (!selectedFile?.endsWith(".py") || !blocklyEditor) return;

        const onChangeHandler = (e: any) => {
            // const updatedCode = blocklyEditor.getCode().trim();
            console.log("code change listner: ", selectedFile)
            setFileStates(prevState => ({ ...prevState, [selectedFile]: e.value }))
        };

        blocklyEditorRef.current?.addChangeListener(onChangeHandler);

        return () => {
            if (blocklyEditorRef.current) {
                blocklyEditorRef.current?.removeChangeListener(onChangeHandler);
                console.log("------removing change listner---------")
            }
        }
    }, [blocklyEditor, selectedFile])

    useEffect(() => {
        // Fetch all commits for existing block
        if (!updateBlockId || !updateBlockName || !blockType) return;

        setBasicInfo(prevInfo => ({ ...prevInfo, name: updateBlockName }));

        getAllCommitsList(updateBlockName, ReposByBlockType[blockType])
            .then((list) => {
                setBlockVersion(updateBlockId);

                if (list) { // updating description based on selected commit
                    list.forEach(item => {
                        if (item.commitId === updateBlockId) {
                            setBasicInfo((prevInfo) => ({ ...prevInfo, description: item.commit.message || '' }));
                        }
                    })
                }
            })
    }, [blockType, updateBlockId, updateBlockName]);

    useEffect(() => {
        if (blockType && !Object.values(BLOCK_TYPE).includes(blockType as BLOCK_TYPE)) {
            return alert('WRONG URL! Block type not found.')
        }

        if (blockType) {
            getContentsListForCommit(updateBlockId || 'main', ReposByBlockType[blockType])
                .then((response) => {
                    console.log("response: ", response);
                    setGitTreeContent(response.tree || []);

                    const structuredTree = convertGitTreeToTreeView(response.tree || [], supportedFileType);
                    console.log('----------file structure: ', structuredTree);
                    setFileStructure(structuredTree);

                    const isReadmePresent = structuredTree.find(item => item.id === BLOCK_FILE_PATH.readme);
                    if (isReadmePresent) {
                        setSelectedFile(BLOCK_FILE_PATH.readme);
                        handleFileSelect(BLOCK_FILE_PATH.readme, BLOCK_FILE_PATH.readme)
                    }
                });
        }
    }, [blockType, handleFileSelect, updateBlockId]);

    useEffect(() => {
        // const pyodide = Pyodide.getInstance();
        // setPyodideInstance(pyodide)

        return () => {
        }
    }, []);

    // console.log('filestates: ', fileStates)
    // console.log('file structure: ', fileStructure)
    // console.log('item to delete paths: ', itemsToDeletePath)

    return (
        <PrimaryLayout transparentLoading={isLoading}>
            {/* TOOLBAR */}
            <Box
                pl={2}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderBottomColor: 'divider',
                    height: "50px"
                }}
            >
                <Box display='flex' alignItems='center' height='100%' gap={1.5}>
                    <Box>
                        <Typography variant='subtitle2' component='p'>{basicInfo.name || "Add a Name"}</Typography>
                        <Typography
                            variant='subtitle2'
                            component='p'
                            sx={{
                                maxWidth: 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {basicInfo.description || "Add a Description"}
                        </Typography>
                    </Box>

                    <IconButton aria-label="edit-basic-info" onClick={() => setOpenBasicInfoModal(true)}>
                        <EditOutlinedIcon />
                    </IconButton>

                    {openBasicInfoModal &&
                        <BasicInfoModal
                            disableName={!!updateBlockId}
                            open={openBasicInfoModal}
                            basicInfo={basicInfo}
                            setOpen={setOpenBasicInfoModal}
                            handleModalSubmit={updateBasicInfo}
                        />}
                </Box>

                <Box display='flex' alignItems='center' height='100%'>
                    <Divider orientation='vertical' flexItem />

                    {updateBlockId && <>
                        <Box width={150}>
                            <Select
                                id="block-version-select"
                                fullWidth
                                value={blockVersion}
                                onChange={(e: SelectChangeEvent) => {
                                    if (!updateBlockId) return;

                                    setFileStates({})
                                    // setSelectedFile(BLOCK_FILE_PATH.block_code)
                                    const newId = e.target.value;
                                    const newPath = location.pathname.replace(updateBlockId, newId);
                                    navigate(newPath, { replace: true });
                                }}
                                renderValue={(selected) => selected.slice(0, 10)}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': { py: 1 },
                                        },
                                    },
                                    MenuListProps: {
                                        sx: { pt: 0, pb: 0, },
                                    },
                                }}
                                sx={{
                                    '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                                }}
                            >
                                {commitsList.map(item => (
                                    <MenuItem key={item.commitId} value={item.commitId}>
                                        <Typography variant='body2' width={90}>
                                            {item.commitId.slice(0, 10)}
                                        </Typography>
                                        <Divider orientation='vertical' sx={{ borderColor: '#dddddd', mx: 1 }} flexItem />
                                        <Typography variant='body2' width={150} textAlign='end'>
                                            {getFormattedDate(new Date(item.created))}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Divider orientation='vertical' flexItem />
                    </>}

                    <Box px={1}>
                        <Button variant="text" onClick={handleTestClick} startIcon={<PlayArrowOutlinedIcon />}>
                            test
                        </Button>
                        {openInputParamsModal &&
                            <VariableInputParams
                                open={openInputParamsModal}
                                inputVariables={inputVariables}
                                setOpen={setOpenInputParamsModal}
                                handleModalSubmit={handleRunPython}
                            />
                        }
                    </Box>

                    <Divider orientation='vertical' flexItem />

                    <Box px={1} display='flex' gap={1.5}>
                        <Button
                            variant="text"
                            onClick={handleSaveBlock}
                            startIcon={<SaveOutlinedIcon />}
                        >
                            {updateBlockId ? "update" : "save"}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                // getInputVariablesList()
                                // getOutputVariablesList()
                                console.log('file structure: ', JSON.stringify(fileStructure, null, 2))
                                console.log('file states: ', JSON.stringify(fileStates, null, 2))
                            }}
                            startIcon={<PublishOutlinedIcon />}
                        >
                            publish
                        </Button>
                    </Box>

                    <Divider orientation='vertical' flexItem />

                    <Box px={1}>
                        <CustomTooltip title="Import File">
                            <IconButton onClick={() => pythonFileInputRef.current?.click()} aria-label="upload-flow">
                                <UploadOutlinedIcon />
                            </IconButton>
                        </CustomTooltip>
                        <input
                            // accept='.py,.md,.json,.txt'
                            type='file'
                            ref={pythonFileInputRef}
                            onChange={handleImportBlockCode}
                            hidden
                            multiple
                        />

                        <CustomTooltip title="Export File">
                            <IconButton onClick={handleExportBlockCode} aria-label="download-flow">
                                <DownloadOutlinedIcon />
                            </IconButton>
                        </CustomTooltip>
                    </Box>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Container
                sx={{
                    display: 'flex',
                    flexGrow: 1,
                    padding: "0 !important",
                    maxWidth: '100% !important',
                    height: "calc(100vh - 100px)"
                }}
            >
                {/* Explorer Panel */}
                <Box sx={{ width: 240, minWidth: 240, borderRight: '1px solid', borderRightColor: 'divider' }}>
                    <Box display='flex' justifyContent='center' gap={1.5}>
                        <CustomTooltip title="Edit">
                            <IconButton size="small"
                                onClick={handleEditTreeItem}
                                disabled={Boolean(selectedFile) && Block_Default_Files[blockType || ''].includes(selectedFile)}
                            >
                                <EditIcon />
                            </IconButton>
                        </CustomTooltip>
                        <Divider orientation='vertical' variant='middle' flexItem />
                        <CustomTooltip title="Add File">
                            <IconButton size="small" onClick={handleAddFileClick}>
                                <NoteAddIcon />
                            </IconButton>
                        </CustomTooltip>
                        <Divider orientation='vertical' variant='middle' flexItem />
                        <CustomTooltip title="Add Folder">
                            <IconButton size="small" onClick={handleAddFolderClick}>
                                <CreateNewFolderIcon />
                            </IconButton>
                        </CustomTooltip>
                        <Divider orientation='vertical' variant='middle' flexItem />
                        <CustomTooltip title="Delete">
                            <IconButton
                                onClick={() => setOpenDeleteModalFileStucture(true)}
                                disabled={Boolean(selectedFile) && Block_Default_Files[blockType || ''].includes(selectedFile)}
                            >
                                <DeleteForeverIcon />
                            </IconButton>
                        </CustomTooltip>

                        <ConfirmationModal
                            open={openDeleteModalFileStucture}
                            setOpen={setOpenDeleteModalFileStucture}
                            headTitle={'Delete ' + selectedFile.split('/').pop()}
                            bodyMessage='Are you sure you want to delete, it cannot be undone?'
                            confirmBtnColor='error'
                            handleSubmit={(isDelete) => {
                                if (!isDelete) return;
                                console.log('deleting file: ', selectedFile)
                                setFileStructure((prevData) => handleDeleteTreeItem(prevData, selectedFile, setItemsToDeletePath));
                                setOpenDeleteModalFileStucture(false);
                                deleteFileStatesKeys(selectedFile, setFileStates);
                                setSelectedFile('');
                            }}
                        />
                    </Box>
                    <Divider />
                    <RichTreeView
                        sx={{ px: 0.5 }}
                        apiRef={apiTreeRef}
                        items={fileStructure}
                        defaultExpandedItems={['block']}
                        selectedItems={selectedFile}
                        onSelectedItemsChange={(e, nodeId) => {
                            if (nodeId) {
                                setSelectedFile(nodeId)
                                console.log("selected file: ", nodeId)

                                const treeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(nodeId)

                                if (nodeId.endsWith(".py") && !fileStates[nodeId]) {
                                    setFileStates(prevData => ({ ...prevData, [nodeId]: getSecondaryBlocklycode() }))
                                }
                                if (nodeId.endsWith(".py") && fileStates[nodeId] && blocklyEditor) {
                                    blocklyEditor.setCode(fileStates[nodeId], undefined, false)
                                }

                                if (nodeId.endsWith(".json") && !fileStates[nodeId]) setFileStates(prevData => ({ ...prevData, [nodeId]: "{}" }))
                                if (nodeId.endsWith(".md") && !fileStates[nodeId]) setFileStates(prevData => ({ ...prevData, [nodeId]: "**Hello world!!!**" }))
                                if (nodeId.endsWith(".txt") && !fileStates[nodeId]) setFileStates(prevData => ({ ...prevData, [nodeId]: "add your text here..." }))

                                if (treeItem?.type !== 'folder' && treeItem?.path && !fileStates[nodeId]) {
                                    handleFileSelect(nodeId, treeItem?.path)
                                }
                            }
                        }}
                        // isItemDisabled={(item: CustomTreeViewBaseItem) => !!item.disabled}
                        slots={{ item: CustomTreeItem }}
                        expansionTrigger="iconContainer"
                    />
                </Box>

                <Box sx={{ width: '100%', height: '100%' }}>
                    {/* Editor Panel */}

                    <BlockTypeEditors
                        blockType={blockType}
                        selectedFile={selectedFile}
                        fileStates={fileStates}
                        setFileStates={setFileStates}
                    />
                </Box>

                <AddDialog
                    isDialogOpen={isDialogOpen}
                    isAddingFolder={isAddingFolder}
                    newFolderName={newFolderName}
                    newFileName={newFileName}
                    isEditMode={isEditModeTreeItem}
                    handleCancel={handleCancel}
                    setNewFileName={setNewFileName}
                    setNewFolderName={setNewFolderName}
                    handleCreateNewFile={handleCreateNewFile}
                    handleCreateNewFolder={() => handleCreateNewFolder(
                        selectedFile,
                        newFolderName,
                        apiTreeRef as React.MutableRefObject<any>,
                        setFileStructure,
                        setIsDialogOpen,
                        setNewFolderName
                    )}
                    handleUpdateTreeItemName={() => {
                        const newName = handleEditTreeItemCallBack(
                            selectedFile,
                            apiTreeRef as React.MutableRefObject<any>,
                            setFileStructure,
                            isAddingFolder,
                            newFolderName,
                            newFileName,
                            setFileStates,
                            setItemsToDeletePath
                        );
                        if (newName) {
                            setSelectedFile(newName);
                            handleCancel();
                        }
                    }}
                />
            </Container>
        </PrimaryLayout>
    );
};

export default AddBlock;
